import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { DeviceStoreSelectors, LocationsRouterStoreSelectors, LocationSubset } from '@era/stores';
import { NgbDropdownButtonItem, NgbDropdownItem, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import {
  ContextMenuComponent,
  MasterComponent,
  MasterDetailComponent,
  PanelComponent,
  PanelHeaderComponent,
} from '@rocksoft/cdk';
import { environment } from '@rocksoft/environment';
import { EnvironmentFeatures } from '@rocksoft/shared';
import { RebootDevicesModalContainerComponent } from '../../sub-features/reboot-devices-modal/reboot-devices-modal.container';
import { DeviceHealthOverviewComponent } from '../device-health-overview/device-health-overview.component';
import {
  DEVICE_LOCATION_SUBSET_CONFIGS,
  DeviceCategoryType,
  selectedCategoryTypesFromQuery,
} from '../devices-location-subset/device-location-subset.config';
import { DevicesLocationSubsetContainerComponent } from '../devices-location-subset/devices-location-subset.container';
import { DevicesSyncModalContainerComponent } from '../modals/devices-sync-modal/devices-sync-modal.container';
import { SelectLocationListDevicesContainerComponent } from '../select-location/select-location-list-devices/select-location-list-devices.container';
import { DeviceCategoryTypeFilterComponent } from './device-category-type-filter.component';
import { DeviceHealthState, selectedDeviceHealthStateFromQuery } from './device-health-state.config';
import { DeviceHealthStateFilterComponent } from './device-health-state-filter.component';

const CATEGORY_TYPE_QUERY_PARAM = 'categoryType';
const HEALTH_STATE_QUERY_PARAM = 'healthState';

@Component({
  selector: 'era-location-devices',
  templateUrl: './location-devices.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    PanelComponent,
    PanelHeaderComponent,
    SelectLocationListDevicesContainerComponent,
    DeviceCategoryTypeFilterComponent,
    DeviceHealthStateFilterComponent,
    ContextMenuComponent,
    NgbDropdownButtonItem,
    NgbDropdownItem,
    MasterDetailComponent,
    MasterComponent,
    DevicesLocationSubsetContainerComponent,
    DeviceHealthOverviewComponent,
  ],
})
export class LocationDevicesComponent {
  private readonly modalService = inject(NgbModal);
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  public readonly features: EnvironmentFeatures = environment.features;
  public readonly currentLocation = this.store.selectSignal(LocationsRouterStoreSelectors.selectLocation);
  public readonly currentLocationDevices = this.store.selectSignal(DeviceStoreSelectors.selectCurrentLocationDevices);
  private readonly queryParamMap = toSignal(this.route.queryParamMap, {
    initialValue: this.route.snapshot.queryParamMap,
  });
  public readonly selectedCategoryTypes = computed(() => selectedCategoryTypesFromQuery(this.queryParamMap()));
  public readonly selectedHealthState = computed(() => selectedDeviceHealthStateFromQuery(this.queryParamMap()));

  public readonly filteredDeviceCategories = computed(() => {
    const selected = this.selectedCategoryTypes();

    return DEVICE_LOCATION_SUBSET_CONFIGS.filter(
      (config) => selected.includes(config.type) && config.featureToggle(this.features)
    );
  });

  public updateCategoryTypes(categoryTypes: readonly DeviceCategoryType[]): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [CATEGORY_TYPE_QUERY_PARAM]: categoryTypes.length > 0 ? categoryTypes : '' },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  public updateHealthState(healthState: DeviceHealthState): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [HEALTH_STATE_QUERY_PARAM]: healthState },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  public openRebootRequest(): void {
    const modal = this.modalService.open(RebootDevicesModalContainerComponent, {
      backdrop: 'static',
      centered: true,
      size: 'lg',
    });
    modal.componentInstance.locationSubset = LocationSubset.All;
  }

  public openSyncModalRequest(): void {
    const modal = this.modalService.open(DevicesSyncModalContainerComponent, {
      backdrop: 'static',
      centered: true,
      size: 'lg',
    });
    modal.componentInstance.locationSubset = LocationSubset.All;
  }
}
